import { computed, DestroyRef, effect, inject, Injectable, signal } from '@angular/core'
import { getAuth } from 'firebase/auth'
import { doc, getDoc, getFirestore, onSnapshot, setDoc, Unsubscribe } from 'firebase/firestore'
import { getFirebaseApp } from './firebase'

export type ThemePreference = 'light' | 'dark' | 'system'
type ResolvedTheme = 'light' | 'dark'

const THEME_VALUES: readonly ThemePreference[] = ['light', 'dark', 'system']

function isThemePreference(value: unknown): value is ThemePreference {
  return typeof value === 'string' && THEME_VALUES.includes(value as ThemePreference)
}

function setCrewdleCookie(name: string, value: string): void {
  try {
    const date = new Date()
    date.setTime(date.getTime() + 365 * 24 * 60 * 60 * 1000)
    document.cookie = `crewdle_${name}=${encodeURIComponent(value)};expires=${date.toUTCString()};path=/;domain=.crewdle.ai;SameSite=Lax`
  } catch {}
}

function getCrewdleCookie(name: string): string | null {
  try {
    const prefix = `crewdle_${name}=`
    for (const cookie of document.cookie.split(';')) {
      const trimmed = cookie.trim()
      if (trimmed.startsWith(prefix)) return decodeURIComponent(trimmed.substring(prefix.length))
    }
  } catch {}
  return null
}

/**
 * Shared suite theme contract. `theme` in localStorage synchronizes same-origin
 * tabs, the `.crewdle.ai` cookie synchronizes suite subdomains on focus, and
 * Firestore synchronizes signed-in devices. The root `dark`/`light` class is
 * also the selector used by @crewdle/white-label's mode-specific brand tokens.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly storageKey = 'theme'
  private readonly mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
  private readonly systemDark = signal(this.mediaQuery.matches)
  private userDocUnsub: Unsubscribe | null = null

  readonly preference = signal<ThemePreference>(this.loadPreference())
  readonly resolvedTheme = computed<ResolvedTheme>(() => this.resolve(this.preference()))

  constructor() {
    // Apply synchronously as well as reactively so the Angular boot screen uses
    // the pre-painted theme even before the first scheduled effect flush.
    this.applyTheme(this.resolvedTheme())
    effect(() => this.applyTheme(this.resolvedTheme()))

    this.mediaQuery.addEventListener('change', this.onSystemThemeChange)
    window.addEventListener('storage', this.onStorage)
    document.addEventListener('visibilitychange', this.onVisibilityChange)
    inject(DestroyRef).onDestroy(() => {
      this.mediaQuery.removeEventListener('change', this.onSystemThemeChange)
      window.removeEventListener('storage', this.onStorage)
      document.removeEventListener('visibilitychange', this.onVisibilityChange)
      this.stopUserSync()
    })
  }

  setPreference(pref: ThemePreference): void {
    this.setLocalPreference(pref)
    setCrewdleCookie('theme', pref)
    void this.syncToFirestore(pref)
  }

  private readonly onSystemThemeChange = (event: MediaQueryListEvent): void => {
    this.systemDark.set(event.matches)
    if (this.preference() === 'system') this.applyTheme(this.resolve('system'))
  }

  private readonly onStorage = (event: StorageEvent): void => {
    if (event.key !== this.storageKey || !isThemePreference(event.newValue)) return
    // Native storage events are delivered to the other tabs on this origin.
    // Mirror them into the cross-subdomain cookie without writing Firestore.
    this.applyRemotePreference(event.newValue)
  }

  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState !== 'visible') return
    const cookie = getCrewdleCookie('theme')
    if (isThemePreference(cookie) && cookie !== this.preference()) {
      this.setLocalPreference(cookie)
    }
  }

  private setLocalPreference(pref: ThemePreference): void {
    this.preference.set(pref)
    try {
      localStorage.setItem(this.storageKey, pref)
    } catch {}
    this.applyTheme(this.resolve(pref))
  }

  /** Apply a preference received from another tab/device without echoing it to Firestore. */
  private applyRemotePreference(pref: ThemePreference): void {
    if (pref !== this.preference()) this.setLocalPreference(pref)
    setCrewdleCookie('theme', pref)
  }

  startUserSync(uid: string): void {
    const app = getFirebaseApp()
    if (!app) return
    this.stopUserSync()
    const db = getFirestore(app)
    this.userDocUnsub = onSnapshot(doc(db, 'users', uid), (snap) => {
      if (!snap.exists()) return
      const remote = snap.data()['theme']
      if (isThemePreference(remote)) this.applyRemotePreference(remote)
    })
  }

  stopUserSync(): void {
    this.userDocUnsub?.()
    this.userDocUnsub = null
  }

  cycle(): void {
    const current = THEME_VALUES.indexOf(this.preference())
    this.setPreference(THEME_VALUES[(current + 1) % THEME_VALUES.length])
  }

  async loadUserTheme(): Promise<void> {
    try {
      const app = getFirebaseApp()
      if (!app) return
      const uid = getAuth(app).currentUser?.uid
      if (!uid) return

      const localTheme = this.readStoredPreference()
      const db = getFirestore(app)
      const userRef = doc(db, 'users', uid)
      const snapshot = await getDoc(userRef)
      const firestoreTheme = snapshot.exists() ? snapshot.data()['theme'] : null

      if (localTheme) {
        if (firestoreTheme !== localTheme) await setDoc(userRef, { theme: localTheme }, { merge: true })
        setCrewdleCookie('theme', localTheme)
      } else if (isThemePreference(firestoreTheme)) {
        this.applyRemotePreference(firestoreTheme)
      }
    } catch {}
  }

  private loadPreference(): ThemePreference {
    return this.readStoredPreference() ?? this.readCookiePreference() ?? 'system'
  }

  private readStoredPreference(): ThemePreference | null {
    try {
      const saved = localStorage.getItem(this.storageKey)
      return isThemePreference(saved) ? saved : null
    } catch {
      return null
    }
  }

  private readCookiePreference(): ThemePreference | null {
    const cookie = getCrewdleCookie('theme')
    return isThemePreference(cookie) ? cookie : null
  }

  private resolve(pref: ThemePreference): ResolvedTheme {
    return pref === 'system' ? (this.systemDark() ? 'dark' : 'light') : pref
  }

  private applyTheme(theme: ResolvedTheme): void {
    const html = document.documentElement
    html.classList.remove('dark', 'light', 'system')
    html.classList.add(theme)
    html.style.colorScheme = theme
    document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')?.setAttribute(
      'content',
      theme === 'dark' ? '#18191A' : '#ffffff',
    )
  }

  private async syncToFirestore(pref: ThemePreference): Promise<void> {
    try {
      const app = getFirebaseApp()
      if (!app) return
      const uid = getAuth(app).currentUser?.uid
      if (!uid) return
      await setDoc(doc(getFirestore(app), 'users', uid), { theme: pref }, { merge: true })
    } catch {}
  }
}
